import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, MessageSquareText } from "lucide-react";
import { PHONE, PHONE_RAW } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        // keep polling a few times
      }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 1500);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="flex min-h-screen items-center justify-center px-4 py-16">
      <div className="w-full max-w-md border border-stone-800 bg-charcoal p-8 text-center">
        <p className="font-display text-2xl font-black uppercase tracking-tight text-bone">
          Friday<span className="text-amber-500">Stumps</span>
        </p>
        {state === "checking" && (
          <p data-testid="payment-checking" className="mt-8 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…
          </p>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mt-8 h-10 w-10 text-green-400" />
            <p data-testid="payment-confirmed" className="mt-4 font-display text-2xl font-black uppercase text-bone">Payment received.</p>
            <p className="mt-3 text-sm leading-relaxed text-stone-400">
              That's everything — your job is wrapped up and Eric has been notified. Thanks for choosing FridayStumps.
            </p>
          </>
        )}
        {(state === "pending" || state === "error") && (
          <>
            <p data-testid="payment-pending" className="mt-8 font-display text-xl font-black uppercase text-bone">Hang tight.</p>
            <p className="mt-3 text-sm leading-relaxed text-stone-400">
              Your payment is still being confirmed. If you completed checkout, you're all set — otherwise text {PHONE} and Eric will sort it out.
            </p>
          </>
        )}
        <a href={`sms:${PHONE_RAW}`} className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500 transition-colors duration-150 hover:text-amber-500">
          <MessageSquareText className="h-3.5 w-3.5" /> Questions? Text {PHONE}
        </a>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="flex min-h-screen items-center justify-center px-4 py-16">
      <div className="w-full max-w-md border border-stone-800 bg-charcoal p-8 text-center">
        <p className="font-display text-2xl font-black uppercase tracking-tight text-bone">
          Friday<span className="text-amber-500">Stumps</span>
        </p>
        <p data-testid="payment-cancelled" className="mt-8 font-display text-xl font-black uppercase text-bone">No charge made.</p>
        <p className="mt-3 text-sm leading-relaxed text-stone-400">
          The checkout was closed before payment went through. If that was an accident, just reopen the payment link Eric texted you — or text {PHONE}.
        </p>
        <a href={`sms:${PHONE_RAW}`} className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500 transition-colors duration-150 hover:text-amber-500">
          <MessageSquareText className="h-3.5 w-3.5" /> Text {PHONE}
        </a>
      </div>
    </div>
  );
}
